import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Login from './pages/Login';
import Sidebar from './components/Sidebar';
import TopBar from './components/TopBar';
import ChatWindow from './components/ChatWindow';
import CreateGroupModal from './components/CreateGroupModal';
import { generateId } from './utils/helpers';
import APP_CONFIG from './config';
import {
  buildAgentFromServer,
  createAgentOnServer,
  duplicateAgentOnServer,
  fetchHealth,
  fetchKernel,
  saveKernel,
  fetchScrollPosition,
  fetchSession,
  hydrateAgents,
  loginUser,
  postMessageStream,
  saveScrollPosition,
  clearSessionHistory,
  sessionHistoryToMessages,
  verifyToken,
  createSubChatOnServer,
  renameAgentOnServer,
  renameSubChatOnServer,
  deleteSubChatOnServer,
  deleteAgentOnServer,
  changePassword,
  createUserAsAdmin,
  deleteUserAsAdmin,
  exportAllData,
  exportExcelData,
  listUsersAsAdmin,
} from './services/veychatApi';
import './App.css';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    try { return localStorage.getItem('veychat_auth') === 'true' && Boolean(localStorage.getItem('veychat_token')); } catch { return false; }
  });
  const [currentUser, setCurrentUser] = useState(() => {
    try { return localStorage.getItem('veychat_user') || null; } catch { return null; }
  });
  const [currentUserRole, setCurrentUserRole] = useState(() => {
    try { return localStorage.getItem('veychat_user_role') || 'user'; } catch { return 'user'; }
  });
  const [currentUserId, setCurrentUserId] = useState(() => {
    try { return localStorage.getItem('veychat_user_id') || null; } catch { return null; }
  });
  const [token, setToken] = useState(() => {
    try { return localStorage.getItem('veychat_token'); } catch { return null; }
  });
  // Validate stored token silently on first load
  useEffect(() => {
    const token = (() => { try { return localStorage.getItem('veychat_token'); } catch { return null; } })();
    if (!token) {
      try {
        localStorage.removeItem('veychat_auth');
        localStorage.removeItem('veychat_user');
        localStorage.removeItem('veychat_user_role');
        localStorage.removeItem('veychat_user_id');
      } catch { /* ignore */ }
      setIsAuthenticated(false);
      setCurrentUser(null);
      setCurrentUserRole('user');
      setCurrentUserId(null);
      return;
    }
    verifyToken(token).then((user) => {
      if (user) {
        try {
          localStorage.setItem('veychat_auth', 'true');
          localStorage.setItem('veychat_user', user.username);
          localStorage.setItem('veychat_user_role', user.role || 'user');
          if (user.id) localStorage.setItem('veychat_user_id', user.id);
        } catch { /* ignore */ }
        setCurrentUser(user.username);
        setCurrentUserRole(user.role || 'user');
        setCurrentUserId(user.id || null);
        setToken(token);
        setIsAuthenticated(true);
      } else {
        // Token expired / invalid — clear everything
        try {
          localStorage.removeItem('veychat_token');
          localStorage.removeItem('veychat_auth');
          localStorage.removeItem('veychat_user');
          localStorage.removeItem('veychat_user_role');
          localStorage.removeItem('veychat_user_id');
        } catch { /* ignore */ }
        setIsAuthenticated(false);
        setCurrentUser(null);
        setCurrentUserRole('user');
        setCurrentUserId(null);
        setToken(null);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply theme from config on mount
  useEffect(() => {
    if (APP_CONFIG.THEME) {
      const theme = APP_CONFIG.THEME;
      const root = document.documentElement;

      // Apply each theme color if it's not null
      if (theme.accentPrimary) root.style.setProperty('--color-accent-primary', theme.accentPrimary);
      if (theme.accentSecondary) root.style.setProperty('--color-accent-secondary', theme.accentSecondary);
      if (theme.accentTertiary) root.style.setProperty('--color-accent-tertiary', theme.accentTertiary);
      if (theme.bgPrimary) root.style.setProperty('--color-bg-primary', theme.bgPrimary);
      if (theme.bgSecondary) root.style.setProperty('--color-bg-secondary', theme.bgSecondary);
      if (theme.bgTertiary) root.style.setProperty('--color-bg-tertiary', theme.bgTertiary);
      if (theme.textPrimary) root.style.setProperty('--color-text-primary', theme.textPrimary);
      if (theme.textSecondary) root.style.setProperty('--color-text-secondary', theme.textSecondary);
    }
  }, []);

  const [agents, setAgents] = useState([]);
  const [groups, setGroups] = useState([]);
  const [messages, setMessages] = useState({});
  const [useLiveApi, setUseLiveApi] = useState(false);
  const [hydrating, setHydrating] = useState(false);
  const [sendingForChatId, setSendingForChatId] = useState(null);
  const [activeChat, setActiveChat] = useState(null);
  const [currentTab, setCurrentTab] = useState('chat');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedAgents, setExpandedAgents] = useState(new Set());
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [selectedModels, setSelectedModels] = useState({});
  const [showNewAgentDialog, setShowNewAgentDialog] = useState(false);
  const [newAgentName, setNewAgentName] = useState('');
  const [newAgentRole, setNewAgentRole] = useState('');
  const [newAgentPersonality, setNewAgentPersonality] = useState('');
  const [sidebarActiveView, setSidebarActiveView] = useState('chats');
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [globalSearchTerm, setGlobalSearchTerm] = useState('');
  const [globalSearchResults, setGlobalSearchResults] = useState([]);
  // Map of agentId -> restored scroll position (pixels)
  const [scrollPositions, setScrollPositions] = useState({});
  const [activityLogs, setActivityLogs] = useState([
    {
      id: 'initial-agent',
      type: 'success',
      title: 'Agent Created',
      description: 'New agent "Agent-1" was created',
      timestamp: '2 minutes ago',
    },
    {
      id: 'initial-message',
      type: 'info',
      title: 'Message Sent',
      description: 'Sent message to Agent-1',
      timestamp: '5 minutes ago',
    },
    {
      id: 'initial-model',
      type: 'warning',
      title: 'Model Changed',
      description: 'Switched to GPT-4o model',
      timestamp: '10 minutes ago',
    },
  ]);

  const browserClientId = useMemo(() => {
    const storageKey = 'veychat_browser_client_id';
    const validId = /^[a-z0-9_-]{8,32}$/;
    try {
      const existing = localStorage.getItem(storageKey);
      if (existing && validId.test(existing)) return existing;

      const randomRaw =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID().replace(/-/g, '')
          : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
      const generated = `c${randomRaw}`.toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 24);
      localStorage.setItem(storageKey, generated);
      return generated;
    } catch {
      return 'clientfallback';
    }
  }, []);

  const backendSessionId = useMemo(() => {
    if (!currentUser) return null;
    const validId = /^[a-zA-Z0-9_-]{8,64}$/;
    if (currentUserId && validId.test(currentUserId)) return currentUserId;

    const storageKey = `veychat_session_id_${String(currentUser).toLowerCase()}`;
    try {
      const existing = localStorage.getItem(storageKey);
      if (existing && validId.test(existing)) return existing;

      const generated =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
      const sessionId = generated.replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 64);
      localStorage.setItem(storageKey, sessionId);
      return sessionId;
    } catch {
      return browserClientId;
    }
  }, [currentUser, currentUserId, browserClientId]);

  const addActivityLog = (entry) => {
    setActivityLogs((prev) => [
      {
        id: generateId('log'),
        timestamp: 'Just now',
        ...entry,
      },
      ...prev,
    ].slice(0, 50));
  };

  useEffect(() => {
    if (!isAuthenticated) {
      setUseLiveApi(false);
      setHydrating(false);
      return;
    }

    let cancelled = false;
    setHydrating(true);

    (async () => {
      try {
        const [, hydration] = await Promise.all([
          fetchHealth().catch(() => ({ status: 'ok' })),
          hydrateAgents(backendSessionId, token)
        ]);
        if (cancelled) return;

        const { agents: rawAgents } = hydration;

        if (!rawAgents || rawAgents.length === 0) {
          setAgents([]);
          setMessages({});
          setGroups([]);
          setUseLiveApi(true);
          return;
        }

        const builtAgents = rawAgents.map((a) => buildAgentFromServer(a.id, a));
        const msgMap = {};
        const scrollMap = {};

        rawAgents.forEach((a) => {
          msgMap[a.id] = sessionHistoryToMessages(a.id, a.session.history || []);
          scrollMap[a.id] = a.scroll_position || 0;
        });

        setAgents(builtAgents);
        setMessages(msgMap);
        setScrollPositions(scrollMap);
        setGroups([]);
        setUseLiveApi(true);
      } catch (err) {
        if (!cancelled) {
          console.error('Hydration error:', err);
          setAgents([]);
          setMessages({});
          setGroups([]);
          setUseLiveApi(false);
        }
      } finally {
        if (!cancelled) setHydrating(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, backendSessionId, token]);

  useEffect(() => {
    if (!isAuthenticated || hydrating) return;
    if (agents.length > 0 && !activeChat) {
      setActiveChat({ type: 'agent', id: agents[0].id });
    }
  }, [isAuthenticated, agents, activeChat, hydrating]);

  useEffect(() => {
    if (!activeChat || !useLiveApi || hydrating) return;
    if (messages[activeChat.id]) return;

    (async () => {
      try {
        const agentId = activeChat.type === 'subChat' ? activeChat.parentId : activeChat.id;
        const subChatId = activeChat.type === 'subChat' ? activeChat.id : null;
        
        const session = await fetchSession(agentId, backendSessionId, token, subChatId);
        const scrollPosition = await fetchScrollPosition(agentId, backendSessionId, token, subChatId).catch(() => 0);
        const history = session.history || [];
        setMessages(prev => ({
          ...prev,
          [activeChat.id]: sessionHistoryToMessages(agentId, history)
        }));
        setScrollPositions(prev => ({
          ...prev,
          [activeChat.id]: scrollPosition || 0
        }));
      } catch (e) {
        console.error('Failed to fetch session:', e);
      }
    })();
  }, [activeChat, messages, useLiveApi, hydrating, backendSessionId, token]);

  const handleLogin = async (username, password) => {
    try {
      const { token, username: authedUser, id: loginUserId } = await loginUser(username, password);
      const userInfo = await verifyToken(token);
      const userId = userInfo?.id || loginUserId || null;
      try {
        localStorage.setItem('veychat_token', token);
        localStorage.setItem('veychat_auth', 'true');
        localStorage.setItem('veychat_user', authedUser);
        localStorage.setItem('veychat_user_role', userInfo?.role || 'user');
        if (userId) localStorage.setItem('veychat_user_id', userId);
      } catch { /* ignore */ }
      setCurrentUser(authedUser);
      setCurrentUserRole(userInfo?.role || 'user');
      setCurrentUserId(userId);
      setToken(token);
      setIsAuthenticated(true);
      return { ok: true };
    } catch (backendErr) {
      return { ok: false, error: backendErr.message || 'Invalid credentials' };
    }
  };

  const handleLogout = () => {
    try {
      localStorage.removeItem('veychat_auth');
      localStorage.removeItem('veychat_user');
      localStorage.removeItem('veychat_token');
      localStorage.removeItem('veychat_user_role');
      localStorage.removeItem('veychat_user_id');
    } catch { /* ignore */ }
    setIsAuthenticated(false);
    setCurrentUser(null);
    setCurrentUserRole('user');
    setCurrentUserId(null);
    setToken(null);
    setActiveChat(null);
    setExpandedAgents(new Set());
    setIsMobileSidebarOpen(false);
    setAgents([]);
    setGroups([]);
    setMessages({});
    setUseLiveApi(false);
    setHydrating(false);
    setSendingForChatId(null);
  };

  const toggleMobileSidebar = () => {
    setIsMobileSidebarOpen(!isMobileSidebarOpen);
  };

  const closeMobileSidebar = () => {
    setIsMobileSidebarOpen(false);
  };

  const handleGlobalSearch = (searchQuery) => {
    setGlobalSearchTerm(searchQuery);
    
    if (!searchQuery.trim()) {
      setGlobalSearchResults([]);
      setSearchTerm('');
      return;
    }

    const query = searchQuery.toLowerCase();
    const results = [];

    agents.forEach(agent => {
      const agentMessages = messages[agent.id] || [];
      const matchingMessages = agentMessages.filter(msg => 
        msg.content.toLowerCase().includes(query)
      );

      if (matchingMessages.length > 0) {
        results.push({
          type: 'agent',
          id: agent.id,
          name: agent.name,
          avatar: agent.avatar,
          matchCount: matchingMessages.length,
          lastMatch: matchingMessages[matchingMessages.length - 1]
        });
      }

      if (agent.subChats) {
        agent.subChats.forEach(subChat => {
          const subChatMessages = messages[subChat.id] || [];
          const matchingSubChatMessages = subChatMessages.filter(msg =>
            msg.content.toLowerCase().includes(query)
          );

          if (matchingSubChatMessages.length > 0) {
            results.push({
              type: 'subChat',
              id: subChat.id,
              parentId: agent.id,
              name: subChat.name,
              parentName: agent.name,
              avatar: agent.avatar,
              matchCount: matchingSubChatMessages.length,
              lastMatch: matchingSubChatMessages[matchingSubChatMessages.length - 1]
            });
          }
        });
      }
    });

    groups.forEach(group => {
      const groupMessages = messages[group.id] || [];
      const matchingMessages = groupMessages.filter(msg =>
        msg.content.toLowerCase().includes(query)
      );

      if (matchingMessages.length > 0) {
        results.push({
          type: 'group',
          id: group.id,
          name: group.name,
          avatar: group.avatar,
          matchCount: matchingMessages.length,
          lastMatch: matchingMessages[matchingMessages.length - 1]
        });
      }
    });

    setGlobalSearchResults(results);
  };

  const handleSearchResultClick = (result) => {
    if (result.type === 'group') {
      setSidebarActiveView('groups');
      setActiveChat({ type: 'group', id: result.id });
    } else if (result.type === 'agent') {
      setSidebarActiveView('chats');
      setActiveChat({ type: 'agent', id: result.id });
    } else if (result.type === 'subChat') {
      setSidebarActiveView('chats');
      setActiveChat({ type: 'subChat', id: result.id, parentId: result.parentId });
      setExpandedAgents(prev => {
        const newExpanded = new Set(prev);
        newExpanded.add(result.parentId);
        return newExpanded;
      });
    }

    if (globalSearchTerm.trim()) {
      setSearchTerm(globalSearchTerm);
    }
    
    setGlobalSearchTerm('');
    setGlobalSearchResults([]);
    closeMobileSidebar();
  };

  const handleClearGlobalSearch = useCallback(() => {
    setGlobalSearchTerm('');
    setGlobalSearchResults([]);
  }, []);

  const resetNewAgentForm = () => {
    setNewAgentName('');
    setNewAgentRole('');
    setNewAgentPersonality('');
  };

  const handleAddAgent = () => {
    setNewAgentName(`Agent-${agents.length + 1}`);
    setNewAgentRole('');
    setNewAgentPersonality('');
    setShowNewAgentDialog(true);
  };

  const handleCreateAgent = async () => {
    if (!newAgentName.trim()) return;
    const name = newAgentName.trim();
    const role = newAgentRole.trim();
    const personality = newAgentPersonality.trim();

    if (useLiveApi) {
      try {
        await createAgentOnServer({ name, role, personality }, token);
        const kernel = await fetchKernel(name, token).catch(() => ({}));
        const newAgent = buildAgentFromServer(name, kernel);
        setAgents((prev) => [...prev, newAgent]);
        setMessages((prev) => ({ ...prev, [name]: [] }));
        setActiveChat({ type: 'agent', id: name });
        setSidebarActiveView('chats');
        setShowNewAgentDialog(false);
        resetNewAgentForm();
        addActivityLog({
          type: 'success',
          title: 'Agent Created',
          description: `New agent "${name}" was created`,
        });
      } catch (e) {
        alert(e.message || 'Failed to create agent on server');
      }
      return;
    }

    const newAgent = {
      id: generateId('agent'),
      name,
      avatar: APP_CONFIG.DEFAULT_AVATARS.GENERIC,
      status: 'active',
      description: personality || role || 'AI agent',
      role: role || 'General Purpose',
      subChats: [],
    };

    setAgents([...agents, newAgent]);
    setMessages({ ...messages, [newAgent.id]: [] });
    setActiveChat({ type: 'agent', id: newAgent.id });
    setSidebarActiveView('chats');
    setShowNewAgentDialog(false);
    resetNewAgentForm();
    addActivityLog({
      type: 'success',
      title: 'Agent Created',
      description: `New agent "${name}" was created`,
    });
  };

  const handleCancelNewAgent = () => {
    setShowNewAgentDialog(false);
    resetNewAgentForm();
  };

  const handleAddGroup = () => {
    setShowCreateGroupModal(true);
  };

  const handleCreateGroup = (groupData) => {
    const newGroup = {
      id: generateId('group'),
      name: groupData.name,
      avatar: groupData.avatar || '👥',
      description: groupData.description || '',
      members: [
        { type: 'user', id: 'current-user', name: 'You' },
        ...groupData.selectedAgents.map(agentId => {
          const agent = agents.find(a => a.id === agentId);
          return { type: 'agent', id: agentId, name: agent.name };
        })
      ],
      createdAt: new Date().toISOString()
    };

    setGroups([...groups, newGroup]);
    setMessages({ ...messages, [newGroup.id]: [] });
    setActiveChat({ type: 'group', id: newGroup.id });
    setSidebarActiveView('groups');
    setShowCreateGroupModal(false);
  };

  const handleExpandAgent = (agentId) => {
    setExpandedAgents(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(agentId)) {
        newExpanded.delete(agentId);
      } else {
        newExpanded.add(agentId);
      }
      return newExpanded;
    });
  };

  const handleAgentAction = async (action, agentId, data) => {
    if (action === 'duplicate') {
      const agent = agents.find(a => a.id === agentId);
      if (!agent) return;

      if (useLiveApi) {
        try {
          const result = await duplicateAgentOnServer(agentId, token);
          const newName = result.name;
          const kernel = await fetchKernel(newName, token);
          const newAgent = buildAgentFromServer(newName, { kernel });
          setAgents((prev) => [...prev, newAgent]);
          setMessages((prev) => ({ ...prev, [newName]: [] }));
        } catch (e) {
          alert(e.message || 'Failed to duplicate agent on server');
        }
        return;
      }

      const newAgent = {
        ...agent,
        id: generateId('agent'),
        name: `${agent.name}-Copy`,
        avatar: agent.avatar || APP_CONFIG.DEFAULT_AVATARS.GENERIC,
        subChats: []
      };

      setAgents([...agents, newAgent]);
      setMessages({ ...messages, [newAgent.id]: [] });
    } else if (action === 'rename') {
      if (useLiveApi) {
        try {
          await renameAgentOnServer(agentId, data, token);
        } catch (e) {
          alert(e.message || 'Failed to rename agent on server');
          return;
        }
      }
      const updatedAgents = agents.map(a =>
        a.id === agentId
          ? {
              ...a,
              id: data,
              name: data,
              subChats: (a.subChats || []).map(sc => ({ ...sc, parentId: data })),
            }
          : a
      );
      setAgents(updatedAgents);
      setMessages((prev) => {
        if (!Object.prototype.hasOwnProperty.call(prev, agentId)) return prev;
        const next = { ...prev, [data]: prev[agentId] };
        delete next[agentId];
        return next;
      });
      if (activeChat?.type === 'agent' && activeChat.id === agentId) {
        setActiveChat({ type: 'agent', id: data });
      } else if (activeChat?.type === 'subChat' && activeChat.parentId === agentId) {
        setActiveChat({ ...activeChat, parentId: data });
      }
    } else if (action === 'editDetails') {
      const agent = agents.find(a => a.id === agentId);
      if (!agent) return;
      const role = data?.role || '';
      const personality = data?.personality || '';

      if (useLiveApi) {
        try {
          const existingKernel = await fetchKernel(agentId, token).catch(() => ({}));
          await saveKernel(agentId, {
            ...existingKernel,
            name: agentId,
            role,
            personality,
          }, token);
        } catch (e) {
          alert(e.message || 'Failed to update agent details');
          return;
        }
      }

      setAgents((prev) => prev.map((item) => (
        item.id === agentId
          ? { ...item, role, description: personality || role || 'AI agent' }
          : item
      )));
    } else if (action === 'createSubChat') {
      const agent = agents.find(a => a.id === agentId);
      if (!agent) return;

      let newSubChat;
      if (useLiveApi) {
        try {
          const scName = `Workspace ${(agent.subChats?.length || 0) + 1}`;
          const result = await createSubChatOnServer(agentId, scName, token, backendSessionId);
          newSubChat = {
            id: result.id,
            name: result.name,
            parentId: agentId
          };
        } catch (e) {
          alert('Failed to create sub-chat on server');
          return;
        }
      } else {
        newSubChat = {
          id: generateId('subchat'),
          name: `Workspace ${(agent.subChats?.length || 0) + 1}`,
          parentId: agentId
        };
      }

      const updatedAgents = agents.map(a =>
        a.id === agentId
          ? { ...a, subChats: [...(a.subChats || []), newSubChat] }
          : a
      );

      setAgents(updatedAgents);
      setMessages({ ...messages, [newSubChat.id]: [] });
      setExpandedAgents(prev => {
        const newExpanded = new Set(prev);
        newExpanded.add(agentId);
        return newExpanded;
      });
      setActiveChat({ type: 'subChat', id: newSubChat.id, parentId: agentId });
    } else if (action === 'renameSubChat') {
      if (useLiveApi) {
        try {
          await renameSubChatOnServer(data.subChatId, data.newName, token);
        } catch (e) {
          console.error('Failed to rename sub-chat on server:', e);
        }
      }
      const updatedAgents = agents.map(a => {
        if (a.id === agentId && a.subChats) {
          return {
            ...a,
            subChats: a.subChats.map(sc =>
              sc.id === data.subChatId ? { ...sc, name: data.newName } : sc
            )
          };
        }
        return a;
      });
      setAgents(updatedAgents);
    } else if (action === 'deleteSubChat') {
      const subChatId = data;
      if (useLiveApi) {
        try {
          await deleteSubChatOnServer(subChatId, token);
        } catch (e) {
          console.error('Failed to delete sub-chat on server:', e);
        }
      }
      const updatedAgents = agents.map(a => {
        if (a.id === agentId && a.subChats) {
          return {
            ...a,
            subChats: a.subChats.filter(sc => sc.id !== subChatId)
          };
        }
        return a;
      });
      setAgents(updatedAgents);
      const newMessages = { ...messages };
      delete newMessages[subChatId];
      setMessages(newMessages);
      if (activeChat?.id === subChatId) {
        setActiveChat(null);
      }
    } else if (action === 'delete') {
      if (useLiveApi) {
        try {
          await deleteAgentOnServer(agentId, token);
        } catch (e) {
          console.error('Failed to delete agent on server:', e);
        }
      }
      setAgents(agents.filter(a => a.id !== agentId));
      const newMessages = { ...messages };
      delete newMessages[agentId];
      const agent = agents.find(a => a.id === agentId);
      if (agent?.subChats) {
        agent.subChats.forEach(sc => {
          delete newMessages[sc.id];
        });
      }
      setMessages(newMessages);
      if (activeChat?.id === agentId) {
        setActiveChat(null);
      }
      setExpandedAgents(prev => {
        const newExpanded = new Set(prev);
        newExpanded.delete(agentId);
        return newExpanded;
      });
    } else if (action === 'renameGroup') {
      const updatedGroups = groups.map(g =>
        g.id === agentId ? { ...g, name: data } : g
      );
      setGroups(updatedGroups);
    } else if (action === 'deleteGroup') {
      const groupId = agentId;
      setGroups(groups.filter(g => g.id !== groupId));
      const newMessages = { ...messages };
      delete newMessages[groupId];
      setMessages(newMessages);
      if (activeChat?.id === groupId) {
        setActiveChat(null);
      }
    }
  };

  const handleModelChange = (chatId, model) => {
    setSelectedModels(prev => ({
      ...prev,
      [chatId]: model
    }));
    addActivityLog({
      type: 'warning',
      title: 'Model Changed',
      description: `Switched to ${model} model`,
    });
  };

  const getCurrentModel = () => {
    if (!activeChat) return null;
    return selectedModels[activeChat.id] || 'gpt-4o';
  };

  const handleSaveScroll = (position) => {
    if (!activeChat || !useLiveApi) return;
    const agentId = activeChat.type === 'subChat' ? activeChat.parentId : activeChat.id;
    const subChatId = activeChat.type === 'subChat' ? activeChat.id : null;
    if (agents.find(a => a.id === agentId)) {
      saveScrollPosition(agentId, backendSessionId, position, token, subChatId);
      setScrollPositions(prev => ({ ...prev, [activeChat.id]: position }));
    }
  };

  const handleChangePassword = async ({ oldPassword, newPassword }) => {
    try {
      await changePassword(oldPassword, newPassword, token);
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err.message || 'Failed to change password' };
    }
  };

  const handleCreateUser = async ({ username, password, role }) => {
    try {
      const result = await createUserAsAdmin({ username, password, role }, token);
      return { ok: true, message: result.message || `User "${username}" created.` };
    } catch (err) {
      return { ok: false, error: err.message || 'Failed to create user' };
    }
  };

  const handleListUsers = async () => {
    try {
      const users = await listUsersAsAdmin(token);
      return { ok: true, users };
    } catch (err) {
      return { ok: false, error: err.message || 'Failed to load users' };
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await deleteUserAsAdmin(userId, token);
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err.message || 'Failed to remove user' };
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handleExportData = async () => {
    const blob = await exportAllData(token);
    downloadBlob(blob, 'veychat_export.json');
  };

  const handleExportExcel = async () => {
    const blob = await exportExcelData(token);
    downloadBlob(blob, 'veychat_export.xlsx');
  };

  const handleSendMessage = ({ content, replyTo, attachments = [] }) => {
    if (!activeChat) return;
    const chatId = activeChat.id;
    const currentModel = getCurrentModel();
    const attachmentSummary = attachments.length
      ? `\n\nAttachments:\n${attachments.map(file => `- ${file.name} (${file.type || 'file'}, ${Math.ceil(file.size / 1024)} KB)`).join('\n')}`
      : '';
    const outgoingContent = `${content.trim()}${attachmentSummary}`.trim();
    const newMessage = {
      id: generateId('msg'),
      sender: 'user',
      content: outgoingContent,
      timestamp: new Date().toISOString(),
      isPinned: false,
      replyTo,
    };

    const useStream =
      useLiveApi &&
      (activeChat.type === 'agent' || activeChat.type === 'subChat');

    if (useStream) {
      const backendAgent = activeChat.type === 'subChat' ? activeChat.parentId : activeChat.id;
      const assistantId = generateId('msg');
      const assistantPlaceholder = {
        id: assistantId,
        sender: 'agent',
        agentId: backendAgent,
        content: '',
        timestamp: new Date().toISOString(),
        isPinned: false,
        streaming: true,
        model: currentModel,
      };

      setMessages((prev) => ({
        ...prev,
        [chatId]: [...(prev[chatId] || []), newMessage, assistantPlaceholder],
      }));

      setSendingForChatId(chatId);
      let accumulated = '';

      postMessageStream(backendAgent, outgoingContent, {
        sessionId: backendSessionId,
        token,
        subChatId: activeChat.type === 'subChat' ? activeChat.id : null,
        onChunk: (chunk) => {
          accumulated += chunk;
          setMessages((prev) => {
            const list = [...(prev[chatId] || [])];
            const idx = list.findIndex((m) => m.id === assistantId);
            if (idx === -1) return prev;
            const next = [...list];
            next[idx] = { ...next[idx], content: accumulated };
            return { ...prev, [chatId]: next };
          });
        },
        onDone: () => {
          setMessages((prev) => {
            const list = [...(prev[chatId] || [])];
            const idx = list.findIndex((m) => m.id === assistantId);
            if (idx === -1) return prev;
            const next = [...list];
            next[idx] = {
              ...next[idx],
              streaming: false,
              content: accumulated || next[idx].content,
            };
            return { ...prev, [chatId]: next };
          });
          setSendingForChatId(null);
        },
        onError: (err) => {
          const fallback = err?.message || 'Request failed';
          setMessages((prev) => {
            const list = [...(prev[chatId] || [])];
            const idx = list.findIndex((m) => m.id === assistantId);
            if (idx === -1) return prev;
            const next = [...list];
            const existing = next[idx].content || '';
            next[idx] = {
              ...next[idx],
              streaming: false,
              content: existing ? `${existing}\n\n(${fallback})` : fallback,
            };
            return { ...prev, [chatId]: next };
          });
          setSendingForChatId(null);
        },
      });
      addActivityLog({
        type: 'info',
        title: 'Message Sent',
        description: `Sent message to ${backendAgent}`,
      });
      return;
    }

    setMessages((prev) => ({
      ...prev,
      [chatId]: [...(prev[chatId] || []), newMessage],
    }));

    setTimeout(() => {
      let agentMessage;
      if (activeChat.type === 'group') {
        const group = groups.find((g) => g.id === activeChat.id);
        const agentMembers = group?.members.filter((m) => m.type === 'agent') || [];
        if (agentMembers.length > 0) {
          const randomAgent = agentMembers[Math.floor(Math.random() * agentMembers.length)];
          agentMessage = {
            id: generateId('msg'),
            sender: 'agent',
            agentId: randomAgent.id,
            content: `This is a simulated group response using ${currentModel}.`,
            timestamp: new Date().toISOString(),
            isPinned: false,
            model: currentModel,
          };
        }
      } else {
        agentMessage = {
          id: generateId('msg'),
          sender: 'agent',
          agentId: activeChat.type === 'agent' ? activeChat.id : activeChat.parentId,
          content: `This is a simulated agent response using ${currentModel}.`,
          timestamp: new Date().toISOString(),
          isPinned: false,
          model: currentModel,
        };
      }

      if (agentMessage) {
        setMessages((prev) => ({
          ...prev,
          [chatId]: [...(prev[chatId] || []), agentMessage],
        }));
      }
    }, 1000);
    addActivityLog({
      type: 'info',
      title: 'Message Sent',
      description: `Sent message to ${activeChat.type === 'agent' ? activeChat.id : activeChat.parentId || activeChat.id}`,
    });
  };

  const handleDeleteMessage = (messageId) => {
    if (!activeChat) return;
    const chatId = activeChat.id;
    setMessages({
      ...messages,
      [chatId]: messages[chatId].filter(m => m.id !== messageId)
    });
  };

  const handlePinMessage = (messageId) => {
    if (!activeChat) return;
    const chatId = activeChat.id;
    setMessages({
      ...messages,
      [chatId]: messages[chatId].map(m =>
        m.id === messageId ? { ...m, isPinned: !m.isPinned } : m
      )
    });
  };

  const handleClearChat = async () => {
    if (!activeChat) return;
    const chatId = activeChat.id;

    if (useLiveApi && (activeChat.type === 'agent' || activeChat.type === 'subChat')) {
      const agentId = activeChat.type === 'subChat' ? activeChat.parentId : activeChat.id;
      const subChatId = activeChat.type === 'subChat' ? activeChat.id : null;

      try {
        await clearSessionHistory(agentId, backendSessionId, token, subChatId);
      } catch (err) {
        alert(err.message || 'Failed to clear chat');
        return;
      }
    }

    setMessages((prev) => ({
      ...prev,
      [chatId]: [],
    }));
    setScrollPositions((prev) => ({
      ...prev,
      [chatId]: 0,
    }));
    if (sendingForChatId === chatId) {
      setSendingForChatId(null);
    }
    addActivityLog({
      type: 'warning',
      title: 'Chat Cleared',
      description: `Cleared chat for ${activeChat.type === 'subChat' ? activeChat.parentId : activeChat.id}`,
    });
  };

  const getCurrentMessages = () => {
    if (!activeChat) return [];
    return messages[activeChat.id] || [];
  };

  const filteredMessages = getCurrentMessages().filter(msg =>
    msg.content.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className="app">
      <Sidebar
        agents={agents}
        groups={groups}
        hydrating={hydrating}
        activeChat={activeChat}
        onSelectChat={(chat) => {
          setActiveChat(chat);
          setSearchTerm('');
          closeMobileSidebar();
        }}
        onAgentAction={handleAgentAction}
        currentTab={currentTab}
        onTabChange={setCurrentTab}
        expandedAgents={expandedAgents}
        onExpandAgent={handleExpandAgent}
        activeView={sidebarActiveView}
        onViewChange={setSidebarActiveView}
        onAddGroup={handleAddGroup}
        activityLogs={activityLogs}
        onClearLogs={() => setActivityLogs([])}
        currentUserRole={currentUserRole}
        onChangePassword={handleChangePassword}
        onCreateUser={handleCreateUser}
        onListUsers={handleListUsers}
        onDeleteUser={handleDeleteUser}
        onExportData={handleExportData}
        onExportExcel={handleExportExcel}
        isMobileOpen={isMobileSidebarOpen}
        onMobileClose={closeMobileSidebar}
      />

      <div className="main-content">
        <TopBar
          activeChat={activeChat}
          agents={agents}
          groups={groups}
          onSearch={handleGlobalSearch}
          onLogout={handleLogout}
          username={currentUser}
          onAddAgent={handleAddAgent}
          onMobileMenuToggle={toggleMobileSidebar}
          selectedModel={getCurrentModel()}
          onModelChange={(model) => activeChat && handleModelChange(activeChat.id, model)}
          globalSearchTerm={globalSearchTerm}
          globalSearchResults={globalSearchResults}
          onSearchResultClick={handleSearchResultClick}
          onClearSearch={handleClearGlobalSearch}
        />

        <ChatWindow
          messages={filteredMessages}
          allMessages={getCurrentMessages()}
          agents={agents}
          activeChat={activeChat}
          isSending={Boolean(activeChat && sendingForChatId === activeChat.id)}
          onSendMessage={handleSendMessage}
          onDeleteMessage={handleDeleteMessage}
          onReplyMessage={() => {}}
          onPinMessage={handlePinMessage}
          onClearChat={handleClearChat}
          selectedModel={getCurrentModel()}
          onModelChange={(model) => activeChat && handleModelChange(activeChat.id, model)}
          restoredScrollPosition={
            activeChat
              ? (scrollPositions[activeChat.id] ?? null)
              : null
          }
          onSaveScroll={handleSaveScroll}
        />
      </div>

      {showCreateGroupModal && (
        <CreateGroupModal
          agents={agents}
          onClose={() => setShowCreateGroupModal(false)}
          onCreate={handleCreateGroup}
        />
      )}

      {showNewAgentDialog && (
        <>
          <div className="dialog-overlay" onClick={handleCancelNewAgent}></div>
          <div className="new-agent-dialog">
            <div className="dialog-content new-agent-form">
              <h3 className="dialog-title">Create New Agent</h3>
              <p className="dialog-message">
                Name your agent and optionally set its role and personality.
              </p>
              <label className="new-agent-label" htmlFor="new-agent-name">Name</label>
              <input
                id="new-agent-name"
                type="text"
                value={newAgentName}
                onChange={(e) => setNewAgentName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') handleCancelNewAgent();
                }}
                className="new-agent-input"
                placeholder="Agent name"
                autoFocus
              />
              <label className="new-agent-label" htmlFor="new-agent-role">Role</label>
              <input
                id="new-agent-role"
                type="text"
                value={newAgentRole}
                onChange={(e) => setNewAgentRole(e.target.value)}
                className="new-agent-input"
                placeholder="e.g. Research assistant"
              />
              <label className="new-agent-label" htmlFor="new-agent-personality">Personality</label>
              <textarea
                id="new-agent-personality"
                value={newAgentPersonality}
                onChange={(e) => setNewAgentPersonality(e.target.value)}
                className="new-agent-input new-agent-textarea"
                placeholder="Tone, style, and behavioral traits"
                rows={4}
              />
              <div className="dialog-actions">
                <button 
                  className="dialog-button dialog-button-cancel"
                  onClick={handleCancelNewAgent}
                >
                  Cancel
                </button>
                <button 
                  className="dialog-button dialog-button-create"
                  onClick={handleCreateAgent}
                  disabled={!newAgentName.trim()}
                >
                  Create Agent
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default App;
